/**
 * Control API for web mode
 */

import { httpClient } from './httpClient'
import type { ApiResponse } from '../types'

// Scheduled task from API (response format)
export interface ScheduledTask {
  taskID: string
  poolID: string
  powerTarget: number
  startTime: string
  endTime: string
  nextExecution: string
}

/**
 * Submit control signals to a pool
 */
export async function submit(
  poolID: string,
  tasks: Array<{ powerTarget: number; startTime: string; endTime: string }>,
  options?: { unit?: 'W' | 'kW' | 'MW'; idempodencyKey?: string }
): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().put(`/pools/${poolID}/control`, {
      tasks,
      unit: options?.unit || 'W',
      ...(options?.idempodencyKey && { idempodencyKey: options.idempodencyKey }),
    })
    return {
      success: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to submit control signals',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get current control signals for a pool
 */
export async function getCurrent(poolID: string): Promise<ApiResponse<ScheduledTask[]>> {
  try {
    const response = await httpClient.getClient().get<{ tasks: ScheduledTask[] }>(
      `/pools/${poolID}/control`
    )
    return {
      success: true,
      data: response.data.tasks || [],
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get current signals',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Delete all control signals for a pool
 */
export async function deleteAll(poolID: string): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().delete(`/pools/${poolID}/control`)
    return {
      success: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete all signals',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Delete a specific control signal by ID
 */
export async function deleteSignal(poolID: string, signalID: string): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().delete(`/pools/${poolID}/control/${signalID}`)
    return {
      success: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete signal',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}
